import axios from "axios";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000/api",

});

// ---------------------------
// REQUEST INTERCEPTOR
// ---------------------------
apiClient.interceptors.request.use(
  (config) => {
    window.dispatchEvent(new Event("api-start"));

    const token =
      localStorage.getItem("customerToken") ||
      localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    window.dispatchEvent(new Event("api-end"));
    return Promise.reject(error);
  }
);

// ---------------------------
// RESPONSE INTERCEPTOR
// ---------------------------
apiClient.interceptors.response.use(
  (response) => {
    window.dispatchEvent(new Event("api-end"));

    // Automatically sync token if backend re-issued it (e.g. role changed and updated in database)
    const resData = response.data;
    if (resData && (resData.token || resData.accessToken)) {
      const newToken = resData.token || resData.accessToken;
      localStorage.setItem("token", newToken);
      localStorage.setItem("customerToken", newToken);
    }

    return response.data;
  },
  (error) => {
    window.dispatchEvent(new Event("api-end"));

    const statusCode = error?.response?.status;
    const res = error?.response?.data;
    const requestUrl = error?.config?.url || "";

    // 🛡️ CRITICAL: Handle Blob Errors (e.g. Export failure)
    // When downloading files (responseType: 'blob'), JSON errors are hidden in the Blob.
    // We must read/parse them to show the actual error message to the user.
    if (res instanceof Blob && res.type === 'application/json') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            reject({
              message: errorData.message || "Something went wrong",
              status: statusCode,
              code: errorData.code,
              action: errorData.action,
              errors: errorData.errors
            });
          } catch (e) {
            reject({ message: "An error occurred parsing error blob", status: statusCode });
          }
        };
        reader.onerror = () => {
          reject({ message: "An error occurred reading error blob", status: statusCode });
        };
        reader.readAsText(res);
      });
    }

    const tokenExists =
      localStorage.getItem("customerToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken");

    const isAuthApi =
      requestUrl.includes("/login") ||
      requestUrl.includes("/customer-login") ||
      requestUrl.includes("/sellerlogin") ||
      requestUrl.includes("/signup");

    // ✅ Session expired ONLY if:
    // - status is 401
    // - token existed
    // - NOT auth APIs
    // - OR status is 404/400/401 AND message contains "User not found" / "Profile not found" / "Account not found" (case-insensitive)
    const errorMsg = (res?.message || (typeof res === "string" ? res : "")).toLowerCase();
    const isUserNotFound =
      (statusCode === 404 || statusCode === 400 || statusCode === 401) &&
      (errorMsg.includes("user not found") ||
       errorMsg.includes("profile not found") ||
       errorMsg.includes("account not found") ||
       errorMsg.includes("user does not exist") ||
       errorMsg.includes("user with this email does not exist") ||
       errorMsg.includes("user with this mobile number does not exist"));

    if ((statusCode === 401 || isUserNotFound) && tokenExists && !isAuthApi) {
      // Clear all possible auth tokens and user data
      localStorage.removeItem("token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("customerToken");
      localStorage.removeItem("role");
      localStorage.removeItem("user");
      localStorage.removeItem("customerName");
      localStorage.removeItem("customerId");
      localStorage.removeItem("selectedAddress");
      localStorage.removeItem("cart");
      localStorage.removeItem("cartCount");

      window.dispatchEvent(new Event("session-expired"));
    }

    // ✅ Normal API error handling
    if (res?.message) {
      return Promise.reject({
        message: res.message,
        status: statusCode,
        data: res // ✅ Pass through full response for conflict handling etc.
      });
    }

    if (res?.errors?.length) {
      // If backend returns list of {field, message}, extract the first one or join them
      const firstError = res.errors[0];
      const errorMessage = typeof firstError === 'object' ? firstError.message : firstError;

      return Promise.reject({
        message: errorMessage || res.message || "Validation Error",
        status: statusCode,
        errors: res.errors // pass full array for components to use
      });
    }

    return Promise.reject({
      message: "Something went wrong",
      status: statusCode,
    });
  }
);

export const get = (...args) => apiClient.get(...args);
export const post = (...args) => apiClient.post(...args);
export const put = (...args) => apiClient.put(...args);
export const patch = (...args) => apiClient.patch(...args);
export const destroy = (...args) => apiClient.delete(...args);
